import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ScoreRing from "@/components/wishire/ScoreRing";
import { Linkedin, MapPin, Briefcase, ExternalLink, BadgeCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicProfile() {
  const { handle } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/profile/${handle}`);
        setProfile(data);
        if (data?.name) document.title = `${data.name} · Wishire Profile`;
      } catch (e) {
        setError("Profile not found or not public.");
      }
    })();
  }, [handle]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-center px-4">
        <div>
          <p className="font-display text-3xl">{error}</p>
          <a href="/" className="mt-4 inline-block text-[#C2410C] hover:text-stone-900">
            ← Back to Wishire
          </a>
        </div>
      </div>
    );

  if (!profile)
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-stone-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] wsh-grain">
      <header className="border-b border-stone-200 bg-[#FDFBF7]/80 backdrop-blur sticky top-0 z-30">
        <div className="container-x flex items-center justify-between py-4">
          <a href="/" className="flex items-baseline gap-1">
            <span className="font-display text-xl font-bold">Wishire</span>
            <span className="font-display italic text-xs text-stone-500">.ai</span>
            <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#C2410C] inline-block translate-y-[-2px]" />
          </a>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500">
            Public Wishire profile
          </span>
        </div>
      </header>

      <section className="container-x py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-5 mb-8">
              {profile.picture ? (
                <img
                  src={profile.picture}
                  alt={profile.name}
                  className="w-24 h-24 rounded-full object-cover border-2 border-stone-200"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-stone-200 flex items-center justify-center font-display text-4xl text-stone-700">
                  {(profile.name || "?")[0]}
                </div>
              )}
              <div>
                <div className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-mono uppercase tracking-widest">
                  <BadgeCheck size={14} /> Wishire verified
                </div>
                <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">{profile.name}</h1>
                {profile.headline && (
                  <p className="text-stone-600 mt-1 max-w-xl">{profile.headline}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-stone-700 text-sm">
              {profile.target_role && (
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase size={14} /> {profile.target_role}
                </span>
              )}
              {profile.city && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} /> {profile.city}
                </span>
              )}
              {profile.years_experience && (
                <span className="font-mono text-xs uppercase tracking-widest text-stone-500">
                  {profile.years_experience}y experience
                </span>
              )}
              {profile.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#0a66c2] hover:text-stone-900"
                >
                  <Linkedin size={14} /> LinkedIn <ExternalLink size={12} />
                </a>
              )}
            </div>

            {profile.skills?.length > 0 && (
              <div className="mt-10">
                <p className="overline mb-3">Top skills</p>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((s, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full bg-white border border-stone-200 text-sm"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 rounded-3xl bg-white border border-stone-200 p-6 md:p-7">
              <p className="overline mb-3">Wishire Score breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Resume" value={profile.breakdown.resume} />
                <Stat label="Interview" value={profile.breakdown.interview} />
                <Stat label="Skills" value={profile.breakdown.skills} />
                <Stat label="Comms" value={profile.breakdown.communication} />
              </div>
              <p className="mt-5 text-stone-700">
                <span className="overline">Verdict</span>{" "}
                <span className="font-display text-xl text-[#C2410C]">{profile.verdict}</span>
              </p>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-[2rem] bg-stone-900 text-stone-50 p-8 md:p-10 wsh-grain relative overflow-hidden sticky top-24">
              <div
                className="absolute inset-0 opacity-40"
                style={{
                  background:
                    "radial-gradient(500px 320px at 80% 20%, rgba(217,119,6,0.4), transparent 60%)",
                }}
              />
              <div className="relative text-center">
                <span className="overline" style={{ color: "rgba(253,251,247,0.6)" }}>
                  Wishire Score
                </span>
                <div className="mt-4 flex justify-center">
                  <ScoreRing value={profile.wishire_score} size={240} dark />
                </div>
                <p className="mt-6 text-stone-300 max-w-sm mx-auto leading-relaxed">
                  Composite of resume quality, voice interview performance, skill match
                  and communication — refreshed every time {profile.name?.split(" ")[0] || "they"} ship something new.
                </p>
                <a
                  href="/"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium"
                >
                  Hire on Wishire
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-center">
      <p className="font-display text-2xl">{value}</p>
      <p className="overline">{label}</p>
    </div>
  );
}
